/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/ban-types */
import React, { useEffect, useMemo } from 'react';
import { createContext, PropsWithChildren, useCallback, useState } from 'react';
import { nanoid } from 'nanoid';
import { useDesktop } from '@revenuewell/logic-desktop';

type DeviceStatus = 'initial' | 'loading' | 'creating' | 'notcreated' | 'ready' | 'notconfigured';

export interface WebPhoneState {
  isReady: boolean;
  calls: CallVendor[];
}

export interface CallVendor {
  callId: string;
  hasSession: boolean;
  progress: boolean;
  established: boolean;
  ended: boolean;
  held: boolean;
  isAudioMuted: boolean;
  isVideoMuted: boolean;
  primary: boolean;
  inTransfer: boolean;
  direction: string;
  terminating: boolean;
  originating: boolean;
  localIdentity?: string;
  remoteIdentity?: string;
  remoteIdentityUser?: string;
}

export interface Call {
  callId: string;
  progress: 'initiating' | 'established' | 'ended';
  onHold: boolean;
  primary: boolean;
  inTransfer: boolean;
  direction: 'inbound' | 'outbound';
  remoteIdentity?: string;
  initiated: number;
  established?: number;
  isAudioMuted: boolean;
}

export interface WebPhoneStatus {
  isReady: boolean;
  isEnabled: boolean;
  isEnabledUI: boolean;
  microphoneExists: boolean;
}

export interface SortedCallsState {
  onholdCallsIds: string[];
  notOnHoldCallsIds: string[];
}

export interface CallsState {
  allCalls: Record<string, Call>;
  ignoredCalls: Record<string, boolean>;
  collapsedCalls: Record<string, boolean>;
  sortedCalls: SortedCallsState;
}

export interface PhoneState {
  clientId: string;
  deviceControl: {
    deviceStatus: DeviceStatus;
    deviceId?: string;
  };
  webPhoneStatus: WebPhoneStatus;
  calls: CallsState;
  callsRef: React.MutableRefObject<CallsState>;
  activeCallNumPad: {
    isExpanded: boolean;
    callId: string;
  };
  setDeviceId: (id: string) => void;
  setDeviceStatus: (deviceStatus: DeviceStatus) => void;
  setActiveCallNumPadExpanded: (callId: string) => void;
  unsetActiveCallNumPadExpanded: () => void;
  ignoreCall: (callId: string) => void;
  toggleCallCollapse: (callId: string) => void;
  setIsWebPhoneEnabledUI: (isEnabledUI: boolean) => void;
  setIsWebPhoneEnabled: (isEnabled: boolean) => void;
  setMicrophoneExists: (microphoneExists: boolean) => void;
  setIsReady: (isReady: boolean) => void;
  setCalls: React.Dispatch<React.SetStateAction<CallsState>>;
}

export const PhoneContext = createContext<PhoneState>(null!);

/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/ban-types */
export default function PhoneProvider({ children }: PropsWithChildren<{}>) {
  const { isDesktopWidget, isDesktopApplication } = useDesktop();

  const [deviceControl, setDeviceControl] = useState<PhoneState['deviceControl']>({
    deviceStatus: 'initial'
  });
  const [activeCallNumPad, setActiveCallNumPad] = useState<PhoneState['activeCallNumPad']>({
    isExpanded: false,
    callId: ''
  });
  const [webPhoneStatus, setWebPhoneStatus] = useState<PhoneState['webPhoneStatus']>({
    isReady: false,
    isEnabled: false,
    isEnabledUI: false,
    microphoneExists: true
  });
  const [calls, setCalls] = useState<PhoneState['calls']>({
    allCalls: {},
    ignoredCalls: {},
    collapsedCalls: {},
    sortedCalls: {
      onholdCallsIds: [],
      notOnHoldCallsIds: []
    }
  });

  const callsRef = React.useRef(calls);

  useEffect(() => {
    callsRef.current = calls;
  }, [calls]);

  const setDeviceId = (id: string) => {
    setDeviceControl((d) => ({ ...d, deviceId: id }));
  };

  const updateDeviceControl = useCallback((value: Partial<PhoneState['deviceControl']>) => {
    setDeviceControl((d) => ({ ...d, ...value }));
  }, []);

  const toggleCallCollapse = useCallback((callId: string) => {
    setCalls((c) => {
      const oldValue = c.collapsedCalls[callId];
      return { ...c, collapsedCalls: { ...c.collapsedCalls, [callId]: !oldValue } };
    });
  }, []);

  const updateWebPhoneStatus = useCallback((value: Partial<PhoneState['webPhoneStatus']>) => {
    setWebPhoneStatus((s) => ({ ...s, ...value }));
  }, []);

  const setDeviceStatus = useCallback(
    (deviceStatus: DeviceStatus) => {
      updateDeviceControl({ deviceStatus: deviceStatus });
    },
    [updateDeviceControl]
  );

  const setActiveCallNumPadExpanded = useCallback(
    (callId: string) => {
      setActiveCallNumPad({ callId, isExpanded: true });
    },
    [setActiveCallNumPad]
  );

  const unsetActiveCallNumPadExpanded = useCallback(() => {
    setActiveCallNumPad({ callId: '', isExpanded: false });
  }, [setActiveCallNumPad]);

  const ignoreCall = useCallback(
    (callId: string) => {
      setCalls((c) => {
        const newIngoredCalls = { ...c.ignoredCalls };
        newIngoredCalls[callId] = true;
        return { ...c, ignoredCalls: newIngoredCalls };
      });
    },
    [setCalls]
  );

  const setIsWebPhoneEnabledUI = useCallback(
    (isEnabledUI: boolean) => {
      updateWebPhoneStatus({ isEnabledUI });
    },
    [updateWebPhoneStatus]
  );

  const setIsWebPhoneEnabled = useCallback((isEnabled: boolean) => {
    updateWebPhoneStatus({ isEnabled });
  }, []);

  const setMicrophoneExists = useCallback(
    (microphoneExists: boolean) => {
      updateWebPhoneStatus({ microphoneExists });
    },
    [updateWebPhoneStatus]
  );

  const setIsReady = useCallback(
    (isReady: boolean) => {
      updateWebPhoneStatus({ isReady });
    },
    [updateWebPhoneStatus]
  );

  useEffect(() => {
    if (isDesktopApplication() && !isDesktopWidget) return;

    const checkMicrophone = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphone = devices.find((device) => device.kind === 'audioinput');
      setMicrophoneExists(!!microphone);
    };

    checkMicrophone();
    navigator.mediaDevices.addEventListener('devicechange', checkMicrophone);

    return () => navigator.mediaDevices.removeEventListener('devicechange', checkMicrophone);
  }, []);

  const clientId = useMemo(() => nanoid(), [])

  const context = useMemo(
    () => ({
      clientId: clientId,
      deviceControl,
      webPhoneStatus,
      calls,
      callsRef,
      activeCallNumPad,
      setDeviceId,
      toggleCallCollapse,
      setCalls,
      setDeviceStatus: setDeviceStatus,
      setActiveCallNumPadExpanded: setActiveCallNumPadExpanded,
      unsetActiveCallNumPadExpanded: unsetActiveCallNumPadExpanded,
      ignoreCall: ignoreCall,
      setIsWebPhoneEnabledUI: setIsWebPhoneEnabledUI,
      setIsWebPhoneEnabled: setIsWebPhoneEnabled,
      setMicrophoneExists: setMicrophoneExists,
      setIsReady: setIsReady
    }),
    [
      deviceControl,
      webPhoneStatus,
      calls,
      activeCallNumPad,
      setDeviceId,
      toggleCallCollapse,
      setCalls,
      setDeviceStatus,
      setActiveCallNumPadExpanded,
      unsetActiveCallNumPadExpanded,
      ignoreCall,
      setIsWebPhoneEnabledUI,
      setIsWebPhoneEnabled,
      setMicrophoneExists,
      setIsReady
    ]
  );

  return <PhoneContext.Provider value={context}>{children}</PhoneContext.Provider>;
}
